import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useAuth, useOrderRefresh } from "@taunggyi-plus/auth";
import {
  Card,
  ManagementListToolbar,
  PaginatedList,
  RecordFormDialog,
  ServerPagination,
  StatusPill,
  formatRecordDate,
} from "@taunggyi-plus/design-system";
type Ride = {
  id: string;
  status: string;
  customer_name: string;
  pickup_area: string;
  dropoff_area: string;
  fare_minor: number;
  note: string;
  created_at: string;
  cancellation_reason: string;
  event_promotion_id?: string | null;
};
type Driver = {
  id: string;
  display_name: string;
  vehicle_color: string;
  vehicle_number: string;
  photo_data_url: string | null;
  is_available: boolean;
};
type Workspace = {
  driver: Driver | null;
  items: Ride[];
  total: number;
  total_orders: number;
  current_orders: number;
  income: number;
};
export function DriverWorkspace({
  mode = "orders",
}: {
  mode?: "orders" | "profile" | "dashboard" | "income";
}) {
  const { client } = useAuth();
  const [data, setData] = useState<Workspace | null>(null),
    [page, setPage] = useState(0),
    [query, setQuery] = useState(""),
    [status, setStatus] = useState(mode === "income" ? "completed" : "all"),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [action, setAction] = useState<{ ride: Ride; decision: string } | null>(null);
  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    const r = await client.rpc("driver_workspace", { page, query, ride_status: status });
    if (r.error) setError(r.error.message);
    else {
      const next = r.data as Workspace;
      const target = new URLSearchParams(window.location.search).get("record");
      if (target && next.driver && !next.items.some((i) => i.id === target)) {
        const detail = await client
          .from("taxi_orders")
          .select(
            "id,status,customer_name,pickup_area,dropoff_area,fare_minor,note,created_at,cancellation_reason,event_promotion_id",
          )
          .eq("id", target)
          .eq("driver_id", next.driver.id)
          .maybeSingle();
        if (detail.data) next.items.unshift(detail.data as Ride);
      }
      setData((old) => ({
        ...next,
        items:
          page === 0
            ? next.items
            : [
                ...(old?.items ?? []).filter((i) => !next.items.some((n) => n.id === i.id)),
                ...next.items,
              ],
      }));
      setError("");
    }
    setLoading(false);
  }, [client, page, query, status]);
  useEffect(() => {
    const timer = setTimeout(() => void load(), 0);
    return () => clearTimeout(timer);
  }, [load]);
  useOrderRefresh(load);
  async function update(ride: Ride, decision: string, fare: number | null = null, reason = "") {
    if (!client || busy) return;
    setBusy(true);
    const r = await client.rpc("driver_ride_action", { ride_id: ride.id, decision, fare, reason });
    if (r.error) setError(r.error.message);
    else {
      setAction(null);
      await load();
    }
    setBusy(false);
  }
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!action) return;
    const f = new FormData(e.currentTarget);
    await update(
      action.ride,
      action.decision,
      action.decision === "confirmed"
        ? action.ride.event_promotion_id
          ? action.ride.fare_minor
          : Math.round(Number(f.get("fare")) * 100)
        : null,
      String(f.get("reason") ?? ""),
    );
  }
  const d = data?.driver;
  return (
    <section className="filtered-list-stack driver-workspace">
      <header className="route-heading">
        <h1>
          {mode === "profile"
            ? "My vehicle"
            : mode === "dashboard"
              ? "Driver dashboard"
              : mode === "income"
                ? "Ride income"
                : "Orders"}
        </h1>
      </header>
      {error && (
        <p role="alert">
          {error}{" "}
          <button className="button button--secondary" onClick={() => void load()}>
            Retry
          </button>
        </p>
      )}
      {loading && !data && <p role="status">Loading driver workspace…</p>}
      {data && !d && (
        <Card className="management-card">
          Your verified driver account has not been activated. Please contact Admin.
        </Card>
      )}
      {d && (
        <>
          {(mode === "profile" || mode === "dashboard") && (
            <Card className="management-card">
              <div className="taxi-driver-profile">
                {d.photo_data_url && (
                  <img
                    className="hotel-listing-card__image"
                    src={d.photo_data_url}
                    alt={`${d.display_name} portrait`}
                  />
                )}
                <div className="taxi-driver-profile__info">
                  <h3>{d.display_name}</h3>
                  <dl>
                    <div>
                      <dt>Car color</dt>
                      <dd>{d.vehicle_color || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt>Vehicle number</dt>
                      <dd>{d.vehicle_number}</dd>
                    </div>
                  </dl>
                  <button
                    className="button"
                    disabled={busy}
                    onClick={async () => {
                      if (!client) return;
                      setBusy(true);
                      const r = await client.rpc("set_driver_availability", {
                        available: !d.is_available,
                      });
                      if (r.error) setError(r.error.message);
                      else await load();
                      setBusy(false);
                    }}
                  >
                    {d.is_available ? "Go unavailable" : "Go available"}
                  </button>
                  <small>Admin manages your account and vehicle details.</small>
                </div>
              </div>
            </Card>
          )}
          {(mode === "dashboard" || mode === "income") && (
            <div className="workspace-metrics">
              <Card className="management-card">
                <small>Current rides</small>
                <h2>{data.current_orders}</h2>
              </Card>
              <Card className="management-card">
                <small>Completed ride income</small>
                <h2>{(data.income / 100).toLocaleString()} MMK</h2>
              </Card>
            </div>
          )}
          {mode !== "profile" && (
            <>
              <ManagementListToolbar
                query={query}
                onQueryChange={(q) => {
                  setPage(0);
                  setQuery(q);
                }}
                searchLabel="Search rides"
                resultCount={data.total}
                onClear={() => {
                  setQuery("");
                  setStatus(mode === "income" ? "completed" : "all");
                  setPage(0);
                }}
                filters={
                  mode === "income"
                    ? []
                    : [
                        {
                          label: "Status",
                          value: status,
                          onChange: (s) => {
                            setPage(0);
                            setStatus(s);
                          },
                          options: [
                            "all",
                            "pending",
                            "confirmed",
                            "in_progress",
                            "completed",
                            "cancelled",
                            "rejected",
                          ].map((value) => ({ value, label: value.replaceAll("_", " ") })),
                        },
                      ]
                }
              />
              <PaginatedList
                pagination="external"
                items={data.items}
                label="Driver ride pages"
                resetKey={`${query}:${status}`}
                renderItem={(r) => (
                  <Card key={r.id} className="management-card driver-ride-card">
                    <h3>{r.customer_name}</h3>
                    <StatusPill>{r.status.replaceAll("_", " ")}</StatusPill>
                    <p>
                      {r.pickup_area} → {r.dropoff_area}
                    </p>
                    <p>
                      {r.fare_minor > 0
                        ? `${(r.fare_minor / 100).toLocaleString()} MMK`
                        : "Fare to be agreed"}
                    </p>
                    <small>{formatRecordDate(r.created_at, "en", true)}</small>
                    {r.note && <p>{r.note}</p>}
                    {r.cancellation_reason && <p>{r.cancellation_reason}</p>}
                    {r.status === "pending" && (
                      <>
                        <button
                          className="button"
                          disabled={busy}
                          onClick={() => setAction({ ride: r, decision: "confirmed" })}
                        >
                          {r.event_promotion_id ? "Accept festival ride" : "Accept ride & set fare"}
                        </button>
                        <button
                          className="button button--secondary"
                          disabled={busy}
                          onClick={() => setAction({ ride: r, decision: "rejected" })}
                        >
                          Decline ride
                        </button>
                      </>
                    )}
                    {["confirmed", "assigned"].includes(r.status) && (
                      <>
                        <button
                          className="button"
                          disabled={busy}
                          onClick={() => void update(r, "in_progress")}
                        >
                          Start ride
                        </button>
                        <button
                          className="button button--secondary"
                          disabled={busy}
                          onClick={() => setAction({ ride: r, decision: "cancelled" })}
                        >
                          Cancel ride
                        </button>
                      </>
                    )}
                    {r.status === "in_progress" && (
                      <button
                        className="button"
                        disabled={busy}
                        onClick={() => void update(r, "completed")}
                      >
                        Complete ride
                      </button>
                    )}
                  </Card>
                )}
              />
              {data.total > 10 && !new URLSearchParams(window.location.search).has("record") && (
                <ServerPagination
                  page={page}
                  total={data.total}
                  loading={loading}
                  onPage={setPage}
                />
              )}
            </>
          )}
        </>
      )}
      {action && (
        <RecordFormDialog
          open
          hideTrigger
          title={action.decision === "confirmed" ? "Accept ride" : "Ride response"}
          onClose={() => setAction(null)}
        >
          <form className="form-grid" onSubmit={submit}>
            {action.decision === "confirmed" ? (
              <label className="field">
                {action.ride.event_promotion_id ? "Festival fare (MMK)" : "Agreed fare (MMK)"}
                <input
                  type="number"
                  name="fare"
                  min="0.01"
                  max="1000000"
                  step="0.01"
                  defaultValue={
                    action.ride.fare_minor > 0 ? action.ride.fare_minor / 100 : undefined
                  }
                  readOnly={Boolean(action.ride.event_promotion_id)}
                  required
                />
                {action.ride.event_promotion_id && (
                  <small>The festival fare and route are fixed.</small>
                )}
              </label>
            ) : (
              <label className="field">
                Reason
                <textarea name="reason" required minLength={3} maxLength={1000} />
              </label>
            )}
            {error && <p role="alert">{error}</p>}
            <button className="button" disabled={busy}>
              Confirm
            </button>
          </form>
        </RecordFormDialog>
      )}
    </section>
  );
}
